import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { APIProvider } from '@vis.gl/react-google-maps';
import LocationMapPicker from '@/components/LocationMapPicker';
import ImageUpload from '@/components/ImageUpload';
import { CheckCircle2, ChevronRight, ChevronLeft, MessageCircle, X, Send, Loader2, MapPin, User, GraduationCap, ClipboardCheck, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const STEPS = [
  { id: 'school_profile', icon: Building2, label: { en: 'School Profile', ja: '学校プロフィール' } },
  { id: 'location', icon: MapPin, label: { en: 'Location', ja: '所在地' } },
  { id: 'instructor', icon: User, label: { en: 'Instructor', ja: '指導者' } },
  { id: 'course', icon: GraduationCap, label: { en: 'First Course', ja: '最初のコース' } },
  { id: 'review', icon: ClipboardCheck, label: { en: 'Review', ja: '確認' } },
];

const MARTIAL_ARTS = ['Aikido','Iaido','Judo','Jujutsu','Karate','Kendo','Kyudo','Naginata','Sumo','Mixed Martial Arts','Other'];

// ─── AI Chat Panel ─────────────────────────────────────
const AIChatPanel = ({ isOpen, onClose, wizardStep, formContext }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `wizard_${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome message on open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'こんにちは！Train In Japan AIアシスタントです。\n\nI can help you set up your school in **Japanese** or **English**. Ask me anything!\n\n学校の設定をお手伝いします。何でもお聞きください！'
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMsg,
        session_id: sessionId,
        wizard_step: wizardStep,
        context: formContext,
      }, { withCredentials: true });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again. / エラーが発生しました。もう一度お試しください。' }]);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div data-testid="ai-chat-panel" className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-700 to-teal-600 text-white">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <span className="font-semibold text-sm">AI Assistant / AIアシスタント</span>
        </div>
        <button onClick={onClose} className="hover:bg-white/20 rounded p-1 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === 'user'
                ? 'bg-emerald-600 text-white rounded-br-sm'
                : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
            }`}>
              <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                __html: m.content
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>')
              }} />
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-200 bg-white">
        <div className="flex gap-2">
          <input
            data-testid="ai-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask anything... / 何でもお聞きください..."
            className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            disabled={loading}
          />
          <button
            data-testid="ai-chat-send"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-full p-2.5 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Wizard ───────────────────────────────────────
export default function SchoolSetupWizard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [school, setSchool] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Form states
  const [schoolForm, setSchoolForm] = useState({
    name: '', description: '', tagline: '', bio: '', logo_url: '', banner_url: '',
  });
  const [locationForm, setLocationForm] = useState({
    name: '', address: '', city: '', prefecture: '', capacity: '20',
    facilities: '', description: '', latitude: null, longitude: null,
  });
  const [instructorForm, setInstructorForm] = useState({
    name: '', email: '', phone: '', rank: '', years_experience: '',
    bio: '', specialties: '',
  });
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', martial_arts_style: 'Karate',
    category: 'Martial Arts', experience_level: 'beginner', class_type: 'group',
    price: '', currency: 'JPY', capacity: '20',
    start_date: '', end_date: '', daily_start_time: '09:00', daily_end_time: '17:00',
    image_url: '',
  });

  // Saved entity IDs for linking
  const [savedLocationId, setSavedLocationId] = useState(null);
  const [savedInstructorId, setSavedInstructorId] = useState(null);

  // Load existing school data if user already has a school
  useEffect(() => {
    if (user && user.role === 'school') {
      axios.get(`${API}/schools/my/school`, { withCredentials: true })
        .then(res => {
          const s = res.data;
          setSchool(s);
          setSchoolForm({
            name: s.name || '', description: s.description || '',
            tagline: s.tagline || '', bio: s.bio || '',
            logo_url: s.logo_url || '', banner_url: s.banner_url || '',
          });
        })
        .catch(() => {});
    }
  }, [user]);

  const stepId = STEPS[currentStep].id;

  const getFormContext = () => {
    const contexts = { school_profile: schoolForm, location: locationForm, instructor: instructorForm, course: courseForm };
    return contexts[stepId] || {};
  };

  // ── Step Handlers ──
  const saveSchoolProfile = async () => {
    if (!schoolForm.name || !schoolForm.description) {
      toast.error('School name and description are required / 学校名と説明は必須です');
      return false;
    }
    setSaving(true);
    try {
      if (school) {
        await axios.put(`${API}/schools/${school.id}`, {
          name: schoolForm.name, description: schoolForm.description,
          tagline: schoolForm.tagline, bio: schoolForm.bio,
          logo_url: schoolForm.logo_url, banner_url: schoolForm.banner_url,
        }, { withCredentials: true });
        toast.success('School profile updated! / 学校プロフィールを更新しました！');
      }
      setCompletedSteps(prev => new Set([...prev, 0]));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save / 保存に失敗しました');
      return false;
    } finally { setSaving(false); }
  };

  const saveLocation = async () => {
    if (!locationForm.name || !locationForm.address || !locationForm.city || !locationForm.prefecture) {
      toast.error('Please fill in all location fields / すべての所在地フィールドを入力してください');
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        ...locationForm,
        capacity: parseInt(locationForm.capacity) || 20,
        facilities: locationForm.facilities ? locationForm.facilities.split(',').map(f => f.trim()) : [],
      };
      const res = await axios.post(`${API}/schools/${school.id}/locations`, payload, { withCredentials: true });
      setSavedLocationId(res.data.id);
      toast.success('Location saved! Map verified. / 所在地を保存しました！地図確認済み。');
      setCompletedSteps(prev => new Set([...prev, 1]));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save location / 所在地の保存に失敗しました');
      return false;
    } finally { setSaving(false); }
  };

  const saveInstructor = async () => {
    if (!instructorForm.name || !instructorForm.email) {
      toast.error('Instructor name and email are required / 指導者名とメールアドレスは必須です');
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        ...instructorForm,
        years_experience: instructorForm.years_experience ? parseInt(instructorForm.years_experience) : null,
        specialties: instructorForm.specialties ? instructorForm.specialties.split(',').map(s => s.trim()) : [],
      };
      const res = await axios.post(`${API}/schools/${school.id}/instructors`, payload, { withCredentials: true });
      setSavedInstructorId(res.data.id);
      toast.success('Instructor added! / 指導者を追加しました！');
      setCompletedSteps(prev => new Set([...prev, 2]));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save instructor / 指導者の保存に失敗しました');
      return false;
    } finally { setSaving(false); }
  };

  const saveCourse = async () => {
    if (!courseForm.title || !courseForm.description || !courseForm.price || !courseForm.start_date || !courseForm.end_date) {
      toast.error('Please fill all required course fields / 必須のコースフィールドをすべて入力してください');
      return false;
    }
    if (!savedLocationId) {
      toast.error('Please complete the Location step first / 先に所在地ステップを完了してください');
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        ...courseForm,
        location_id: savedLocationId,
        instructor_id: savedInstructorId || undefined,
        price: parseFloat(courseForm.price),
        capacity: parseInt(courseForm.capacity) || 20,
        image_url: courseForm.image_url || 'https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_6095.JPG',
      };
      await axios.post(`${API}/schools/${school.id}/courses`, payload, { withCredentials: true });
      toast.success('Course created and submitted for approval! / コースが作成され、承認のために提出されました！');
      setCompletedSteps(prev => new Set([...prev, 3]));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create course / コースの作成に失敗しました');
      return false;
    } finally { setSaving(false); }
  };

  const handleNext = async () => {
    const savers = [saveSchoolProfile, saveLocation, saveInstructor, saveCourse];
    if (currentStep < 4) {
      const success = await savers[currentStep]();
      if (success) setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  if (!user || user.role !== 'school') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600">Please log in as a school account to use the setup wizard.</p>
            <p className="text-slate-500 text-sm mt-1">学校アカウントでログインしてください。</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-emerald-600 hover:bg-emerald-700">Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900">School Setup Wizard / 学校セットアップ</h1>
            <p className="text-xs text-slate-500">{school?.name || 'Your School'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="ai-assistant-toggle"
              onClick={() => setChatOpen(!chatOpen)}
              variant={chatOpen ? "default" : "outline"}
              className={chatOpen ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-600 text-emerald-700 hover:bg-emerald-50"}
              size="sm"
            >
              <MessageCircle className="w-4 h-4 mr-1.5" />
              AI Help
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const isActive = i === currentStep;
            const isDone = completedSteps.has(i);
            return (
              <React.Fragment key={step.id}>
                <button
                  data-testid={`wizard-step-${step.id}`}
                  onClick={() => i <= Math.max(...completedSteps, 0) + 1 && setCurrentStep(i)}
                  className={`flex flex-col items-center gap-1.5 transition-all ${
                    isActive ? 'scale-105' : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isDone ? 'bg-emerald-600 text-white' :
                    isActive ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-600' :
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {isDone ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {step.label.en}
                  </span>
                  <span className={`text-xs hidden sm:block ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {step.label.ja}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${isDone ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className={`transition-all ${chatOpen ? 'mr-0 sm:mr-96' : ''}`}>
          {/* Step 1: School Profile */}
          {currentStep === 0 && (
            <Card data-testid="step-school-profile">
              <CardHeader>
                <CardTitle className="text-xl">School Profile / 学校プロフィール</CardTitle>
                <CardDescription>Tell students about your school / 学校について教えてください</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>School Name / 学校名 *</Label>
                    <Input data-testid="school-name-input" value={schoolForm.name} onChange={e => setSchoolForm({...schoolForm, name: e.target.value})} placeholder="e.g. Tokyo Karate Dojo" />
                  </div>
                  <div>
                    <Label>Tagline / キャッチフレーズ</Label>
                    <Input data-testid="school-tagline-input" value={schoolForm.tagline} onChange={e => setSchoolForm({...schoolForm, tagline: e.target.value})} placeholder="e.g. Excellence in Martial Arts" />
                  </div>
                </div>
                <div>
                  <Label>Description / 説明 *</Label>
                  <Textarea data-testid="school-description-input" value={schoolForm.description} onChange={e => setSchoolForm({...schoolForm, description: e.target.value})} placeholder="Describe your school, training philosophy, and what makes you unique..." rows={4} />
                </div>
                <div>
                  <Label>Bio / 略歴</Label>
                  <Textarea data-testid="school-bio-input" value={schoolForm.bio} onChange={e => setSchoolForm({...schoolForm, bio: e.target.value})} placeholder="Your school's history and background..." rows={3} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>Logo / ロゴ</Label>
                    <ImageUpload onUpload={url => setSchoolForm({...schoolForm, logo_url: url})} currentImage={schoolForm.logo_url} label="Upload Logo" />
                  </div>
                  <div>
                    <Label>Banner Image / バナー画像</Label>
                    <ImageUpload onUpload={url => setSchoolForm({...schoolForm, banner_url: url})} currentImage={schoolForm.banner_url} label="Upload Banner" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Location */}
          {currentStep === 1 && (
            <Card data-testid="step-location">
              <CardHeader>
                <CardTitle className="text-xl">Training Location / トレーニング施設</CardTitle>
                <CardDescription>Where do you train? Add your facility. / どこでトレーニングしますか？</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>Facility Name / 施設名 *</Label>
                    <Input data-testid="location-name-input" value={locationForm.name} onChange={e => setLocationForm({...locationForm, name: e.target.value})} placeholder="e.g. Main Dojo" />
                  </div>
                  <div>
                    <Label>Capacity / 定員 *</Label>
                    <Input data-testid="location-capacity-input" type="number" value={locationForm.capacity} onChange={e => setLocationForm({...locationForm, capacity: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Address / 住所 *</Label>
                  <Input data-testid="location-address-input" value={locationForm.address} onChange={e => setLocationForm({...locationForm, address: e.target.value})} placeholder="Full address" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>City / 市区町村 *</Label>
                    <Input data-testid="location-city-input" value={locationForm.city} onChange={e => setLocationForm({...locationForm, city: e.target.value})} placeholder="e.g. Shinjuku" />
                  </div>
                  <div>
                    <Label>Prefecture / 都道府県 *</Label>
                    <Input data-testid="location-prefecture-input" value={locationForm.prefecture} onChange={e => setLocationForm({...locationForm, prefecture: e.target.value})} placeholder="e.g. Tokyo" />
                  </div>
                </div>
                <div>
                  <Label>Facilities / 設備 (comma separated)</Label>
                  <Input data-testid="location-facilities-input" value={locationForm.facilities} onChange={e => setLocationForm({...locationForm, facilities: e.target.value})} placeholder="e.g. Changing rooms, Showers, Parking" />
                </div>
                <div>
                  <Label>Description / 説明</Label>
                  <Textarea data-testid="location-description-input" value={locationForm.description} onChange={e => setLocationForm({...locationForm, description: e.target.value})} placeholder="Describe the training space..." rows={2} />
                </div>
                {/* Map */}
                <div>
                  <Label className="text-base font-semibold">Pin Your Location on the Map / 地図で場所を指定</Label>
                  <div className="mt-2">
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places", "geocoding"]}>
                      <LocationMapPicker
                        onLocationSelect={(loc) => {
                          setLocationForm(prev => ({
                            ...prev,
                            address: loc.address || prev.address,
                            city: loc.city || prev.city,
                            prefecture: loc.prefecture || prev.prefecture,
                            latitude: loc.lat,
                            longitude: loc.lng,
                          }));
                        }}
                        initialPosition={locationForm.latitude ? { lat: locationForm.latitude, lng: locationForm.longitude } : undefined}
                        addressToGeocode={locationForm.address}
                      />
                    </APIProvider>
                  </div>
                  {locationForm.latitude && (
                    <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Map pin set / 地図ピン設定済み ({locationForm.latitude.toFixed(4)}, {locationForm.longitude.toFixed(4)})
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Instructor */}
          {currentStep === 2 && (
            <Card data-testid="step-instructor">
              <CardHeader>
                <CardTitle className="text-xl">Add Instructor / 指導者の追加</CardTitle>
                <CardDescription>Who teaches at your school? / あなたの学校の指導者は？</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>Full Name / 氏名 *</Label>
                    <Input data-testid="instructor-name-input" value={instructorForm.name} onChange={e => setInstructorForm({...instructorForm, name: e.target.value})} placeholder="Instructor name" />
                  </div>
                  <div>
                    <Label>Email / メール *</Label>
                    <Input data-testid="instructor-email-input" type="email" value={instructorForm.email} onChange={e => setInstructorForm({...instructorForm, email: e.target.value})} placeholder="instructor@email.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <Label>Phone / 電話</Label>
                    <Input data-testid="instructor-phone-input" value={instructorForm.phone} onChange={e => setInstructorForm({...instructorForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Rank / 段位</Label>
                    <Input data-testid="instructor-rank-input" value={instructorForm.rank} onChange={e => setInstructorForm({...instructorForm, rank: e.target.value})} placeholder="e.g. 5th Dan" />
                  </div>
                  <div>
                    <Label>Years Experience / 経験年数</Label>
                    <Input data-testid="instructor-years-input" type="number" value={instructorForm.years_experience} onChange={e => setInstructorForm({...instructorForm, years_experience: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Bio / 略歴</Label>
                  <Textarea data-testid="instructor-bio-input" value={instructorForm.bio} onChange={e => setInstructorForm({...instructorForm, bio: e.target.value})} placeholder="Instructor background, achievements, teaching style..." rows={3} />
                </div>
                <div>
                  <Label>Specialties / 専門 (comma separated)</Label>
                  <Input data-testid="instructor-specialties-input" value={instructorForm.specialties} onChange={e => setInstructorForm({...instructorForm, specialties: e.target.value})} placeholder="e.g. Kata, Kumite, Self-defense" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Course */}
          {currentStep === 3 && (
            <Card data-testid="step-course">
              <CardHeader>
                <CardTitle className="text-xl">Create Your First Course / 最初のコースを作成</CardTitle>
                <CardDescription>This will be submitted for admin approval / 管理者の承認のために提出されます</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>Course Title / コースタイトル *</Label>
                    <Input data-testid="course-title-input" value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} placeholder="e.g. Beginner Karate Intensive" />
                  </div>
                  <div>
                    <Label>Martial Arts Style / 武道スタイル *</Label>
                    <Select value={courseForm.martial_arts_style} onValueChange={v => setCourseForm({...courseForm, martial_arts_style: v})}>
                      <SelectTrigger data-testid="course-style-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MARTIAL_ARTS.map(ma => <SelectItem key={ma} value={ma}>{ma}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description / 説明 *</Label>
                  <Textarea data-testid="course-description-input" value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} placeholder="What will students learn? Schedule details, requirements..." rows={4} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <Label>Experience Level / レベル</Label>
                    <Select value={courseForm.experience_level} onValueChange={v => setCourseForm({...courseForm, experience_level: v})}>
                      <SelectTrigger data-testid="course-level-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner / 初級</SelectItem>
                        <SelectItem value="intermediate">Intermediate / 中級</SelectItem>
                        <SelectItem value="advanced">Advanced / 上級</SelectItem>
                        <SelectItem value="all">All Levels / 全レベル</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Class Type / 授業形式</Label>
                    <Select value={courseForm.class_type} onValueChange={v => setCourseForm({...courseForm, class_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="group">Group / グループ</SelectItem>
                        <SelectItem value="private">Private / 個人</SelectItem>
                        <SelectItem value="semi-private">Semi-private / セミプライベート</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Capacity / 定員</Label>
                    <Input data-testid="course-capacity-input" type="number" value={courseForm.capacity} onChange={e => setCourseForm({...courseForm, capacity: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <Label>Price / 料金 *</Label>
                    <div className="flex gap-2">
                      <Input data-testid="course-price-input" type="number" value={courseForm.price} onChange={e => setCourseForm({...courseForm, price: e.target.value})} placeholder="50000" />
                      <Select value={courseForm.currency} onValueChange={v => setCourseForm({...courseForm, currency: v})}>
                        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="JPY">JPY</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Start Date / 開始日 *</Label>
                    <Input data-testid="course-start-date" type="date" value={courseForm.start_date} onChange={e => setCourseForm({...courseForm, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>End Date / 終了日 *</Label>
                    <Input data-testid="course-end-date" type="date" value={courseForm.end_date} onChange={e => setCourseForm({...courseForm, end_date: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label>Daily Start Time / 開始時間</Label>
                    <Input type="time" value={courseForm.daily_start_time} onChange={e => setCourseForm({...courseForm, daily_start_time: e.target.value})} />
                  </div>
                  <div>
                    <Label>Daily End Time / 終了時間</Label>
                    <Input type="time" value={courseForm.daily_end_time} onChange={e => setCourseForm({...courseForm, daily_end_time: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Course Image / コース画像</Label>
                  <ImageUpload onUpload={url => setCourseForm({...courseForm, image_url: url})} currentImage={courseForm.image_url} label="Upload Image" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Review */}
          {currentStep === 4 && (
            <Card data-testid="step-review">
              <CardHeader>
                <CardTitle className="text-xl">Review / 確認</CardTitle>
                <CardDescription>Check everything before finishing / 完了前にすべてを確認してください</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* School Summary */}
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h3 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> School Profile
                    {completedSteps.has(0) && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                  <p className="text-sm text-slate-700"><strong>{schoolForm.name}</strong> — {schoolForm.tagline}</p>
                  <p className="text-xs text-slate-500 mt-1">{schoolForm.description?.substring(0, 150)}...</p>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Location
                    {completedSteps.has(1) && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                  {savedLocationId ? (
                    <p className="text-sm text-slate-700">{locationForm.name} — {locationForm.address}, {locationForm.city}, {locationForm.prefecture}</p>
                  ) : (
                    <p className="text-sm text-amber-600">Not yet saved / まだ保存されていません</p>
                  )}
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Instructor
                    {completedSteps.has(2) && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                  {savedInstructorId ? (
                    <p className="text-sm text-slate-700">{instructorForm.name} — {instructorForm.rank} ({instructorForm.years_experience} years)</p>
                  ) : (
                    <p className="text-sm text-amber-600">Not yet saved / まだ保存されていません</p>
                  )}
                </div>

                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> First Course
                    {completedSteps.has(3) && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                  {completedSteps.has(3) ? (
                    <p className="text-sm text-slate-700">{courseForm.title} — {courseForm.martial_arts_style} ({courseForm.price} {courseForm.currency})</p>
                  ) : (
                    <p className="text-sm text-amber-600">Not yet created / まだ作成されていません</p>
                  )}
                </div>

                {completedSteps.size >= 4 && (
                  <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-300 text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-emerald-800">All Steps Complete! / すべて完了！</h3>
                    <p className="text-sm text-slate-600 mt-2">Your course has been submitted for admin approval. You'll be notified once approved.</p>
                    <p className="text-sm text-slate-500">コースは管理者の承認のために提出されました。承認されたら通知されます。</p>
                    <Button data-testid="go-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                      Go to Dashboard / ダッシュボードへ
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6 pb-8">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 0} className="gap-1">
              <ChevronLeft className="w-4 h-4" /> Back / 戻る
            </Button>
            {currentStep < 4 && (
              <Button
                data-testid="wizard-next-btn"
                onClick={handleNext}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {currentStep === 3 ? 'Submit Course / コース提出' : 'Save & Next / 保存して次へ'}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* AI Chat Panel */}
      <AIChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        wizardStep={stepId}
        formContext={getFormContext()}
      />

      {/* Floating AI button (mobile) */}
      {!chatOpen && (
        <button
          data-testid="ai-fab"
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 sm:hidden bg-emerald-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:bg-emerald-700 transition-all z-50"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
